/**
 * SurpriseMePage — Infinite Scroll Discovery Feed
 * ─────────────────────────────────────────────────
 * Cards stack vertically in a continuous feed.
 * An IntersectionObserver sentinel at the bottom triggers
 * appending the next batch of picked projects.
 *
 * Place at: src/pages/SurpriseMePage.jsx
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Shuffle, GitFork, Eye,
  Map as MapIcon, ChevronDown, ChevronRight,
  Sparkles, Compass, X, ExternalLink,
  Package, Palette, Music, Utensils, BookOpen, Stethoscope,
  Cpu, TrendingUp, Layers, CornerDownRight,
  Filter, ArrowUp, CheckSquare,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';
import FeedSelectionBar, { useFeedSelection } from '../components/FeedSelectionBar';
import { useAuth } from '../context/AuthContext';
import { ZONES, PROJECT_TYPES, getProjectZone } from '../config/zones';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const ZONE_ICONS = {
  build: Package, beautiful: Palette, sound: Music, taste: Utensils,
  story: BookOpen, heal: Stethoscope, smart: Cpu, sell: TrendingUp,
};

const DISCOVERY_MODES = [
  { id: 'random',     label: 'Random',       icon: Shuffle,   description: 'Pure serendipity' },
  { id: 'surprising', label: 'Surprising',   icon: Sparkles,  description: 'Wild cross-zone forks' },
  { id: 'crosszone',  label: 'Cross-Zone',   icon: Sparkles,  description: 'Wild connections' },
  { id: 'underrated', label: 'Hidden Gems',  icon: Compass,   description: 'Low views, high forks' },
];

// How many cards to append each time the sentinel is hit
const BATCH_SIZE = 5;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60)     return 'just now';
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
};

// ═══════════════════════════════════════════════════════════════
// SURPRISE CARD
// ═══════════════════════════════════════════════════════════════

const SurpriseCard = ({
  project, index, onFork, onViewOnMap, onViewProject, onFollowRabbitHole,
  onSelect, isSelected,
}) => {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  // Staggered reveal as each card enters viewport
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    if (cardRef.current) obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, []);

  if (!project) return null;

  const zone        = getProjectZone(project);
  const ZoneIcon    = ZONE_ICONS[zone.id] || Compass;
  const projectType = PROJECT_TYPES[project.project_type || project.projectType || 'digital'];
  const imageUrl    = project.image_url || project.imageUrl;
  const forks       = project.fork_count  || project.forkCount  || 0;
  const views       = project.view_count  || project.viewCount  || 0;
  const author      = project.author?.username || project.creator_username || '';
  const desc        = project.description || '';
  const tags        = (project.tags || []).slice(0, 6).map(t => typeof t === 'string' ? t : t.name || '');
  const hasLineage  = project.parent_project_id || project.parent_project_ids?.length > 0 || forks > 0;

  return (
    <div
      ref={cardRef}
      className="relative w-full max-w-lg mx-auto"
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.45s cubic-bezier(0.16,1,0.3,1) ${Math.min(index * 80, 200)}ms,
                     transform 0.45s cubic-bezier(0.16,1,0.3,1) ${Math.min(index * 80, 200)}ms`,
      }}
    >
      {/* Vertical connector line between cards */}
      {index > 0 && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-transparent to-zinc-800" />
      )}

      <div className={cn(
        'relative bg-zinc-900 border rounded-3xl overflow-hidden shadow-2xl shadow-black/40 transition-colors duration-300',
        isSelected ? 'border-purple-500 ring-2 ring-purple-500/30 shadow-purple-500/10' : 'border-zinc-800 hover:border-zinc-700'
      )}>

        {/* Cover image */}
        <div className="relative h-64 sm:h-80 overflow-hidden">
          {/* Select button */}
          {onSelect && (
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(project); }}
              className={cn(
                'absolute top-4 right-4 p-2 rounded-xl z-20 transition-all',
                isSelected
                  ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-zinc-950/60 text-zinc-400 hover:text-white hover:bg-zinc-950/80 backdrop-blur-sm'
              )}
              title={isSelected ? 'Deselect' : 'Select for fork/collect'}
            >
              <CheckSquare className="w-4 h-4" />
            </button>
          )}
          {imageUrl ? (
            <>
              <img src={imageUrl} alt={project.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
            </>
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${zone.color}15 0%, #18181b 60%)` }}
            >
              <div className="text-center">
                <ZoneIcon className="w-16 h-16 mx-auto mb-3" style={{ color: zone.color, opacity: 0.3 }} />
                <span className="text-7xl font-black" style={{ color: zone.color, opacity: 0.08 }}>
                  {project.name?.[0]?.toUpperCase()}
                </span>
              </div>
            </div>
          )}

          {/* Zone + type badges */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-xl"
              style={{ backgroundColor: `${zone.color}25`, color: zone.color, border: `1px solid ${zone.color}40` }}
            >
              <ZoneIcon className="w-3.5 h-3.5" />
              {zone.shortLabel}
            </span>
            <span
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-zinc-900/60 backdrop-blur-xl border border-zinc-700/50"
              style={{ color: projectType?.color }}
            >
              {project.project_type || project.projectType || 'digital'}
            </span>
          </div>

          {/* Lineage button */}
          {hasLineage && (
            <button
              onClick={() => onFollowRabbitHole(project)}
              className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 backdrop-blur-xl hover:bg-purple-500/30 transition-colors"
            >
              <GitFork className="w-3.5 h-3.5" />
              {forks > 0 ? `${forks} fork${forks !== 1 ? 's' : ''}` : 'Has lineage'}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}

          {/* Sequence number pill — subtle, top-right when no lineage */}
          {!hasLineage && (
            <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-mono text-zinc-600 bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/50">
              #{index + 1}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 -mt-8 relative z-10">
          <h2 className="text-2xl font-bold text-white leading-tight mb-2 line-clamp-2">
            {project.name}
          </h2>

          <div className="flex items-center gap-3 mb-4">
            {author && (
              <span className="text-sm text-zinc-400">
                by <span className="text-zinc-300 font-medium">@{author}</span>
              </span>
            )}
            <span className="text-xs text-zinc-600">{timeAgo(project.created_at || project.createdAt)}</span>
          </div>

          {desc && (
            <p className="text-sm text-zinc-400 leading-relaxed mb-4 line-clamp-3">{desc}</p>
          )}

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {tags.map((tag, i) => (
                <span key={i} className="px-2.5 py-1 text-xs rounded-lg bg-zinc-800/80 text-zinc-400 border border-zinc-700/50">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-5 pb-5 border-b border-zinc-800/60">
            <span className="flex items-center gap-1.5">
              <GitFork className="w-4 h-4" /> {forks} fork{forks !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> {views} view{views !== 1 ? 's' : ''}
            </span>
            {hasLineage && (
              <span className="flex items-center gap-1.5 text-purple-400/70">
                <Layers className="w-4 h-4" /> Has lineage
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => onFork(project)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: zone.color, boxShadow: `0 8px 30px -4px ${zone.color}40` }}
            >
              <GitFork className="w-4 h-4" />
              Fork This
            </button>

            <button
              onClick={() => onViewProject(project)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-zinc-300 bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Details
            </button>

            <button
              onClick={() => onViewOnMap(project)}
              className="flex items-center justify-center px-3 py-3 rounded-xl text-zinc-400 bg-zinc-800 border border-zinc-700 hover:border-zinc-600 hover:text-zinc-200 transition-colors"
              title="View on map"
            >
              <MapIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RABBIT HOLE PANEL
// ═══════════════════════════════════════════════════════════════

const RabbitHolePanel = ({ project, projects, onSelect, onClose }) => {
  if (!project || !projects?.length) return null;

  const parentIds  = [project.parent_project_id, ...(project.parent_project_ids || [])].filter(Boolean);
  const parents    = projects.filter(p => parentIds.includes(p.id));
  const children   = projects.filter(p => p.parent_project_id === project.id || (p.parent_project_ids || []).includes(project.id));
  const grandchildren = projects.filter(p =>
    children.some(c => p.parent_project_id === c.id || (p.parent_project_ids || []).includes(c.id))
  );

  const LineageNode = ({ p, depth = 0, isCurrent = false }) => {
    const z   = getProjectZone(p);
    const img = p.image_url || p.imageUrl;
    return (
      <button
        onClick={() => !isCurrent && onSelect(p)}
        className={cn(
          'flex items-center gap-3 p-3 rounded-xl transition-all w-full text-left',
          isCurrent
            ? 'bg-purple-500/15 border border-purple-500/30 cursor-default'
            : 'bg-zinc-800/50 border border-zinc-700/30 hover:bg-zinc-800 hover:border-zinc-600 cursor-pointer'
        )}
        style={{ marginLeft: depth * 16 }}
      >
        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border-2" style={{ borderColor: z.color + '60' }}>
          {img
            ? <img src={img} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm font-bold" style={{ backgroundColor: z.color + '15' }}>{p.name?.[0]}</div>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-medium truncate', isCurrent ? 'text-purple-300' : 'text-white')}>{p.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: z.color + '20', color: z.color }}>{z.shortLabel}</span>
            <span className="text-[10px] text-zinc-600">
              {p.author?.username || p.creator_username ? `@${p.author?.username || p.creator_username}` : ''}
            </span>
          </div>
        </div>
        {!isCurrent && <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[85vh] bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-8 duration-300 z-10">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <CornerDownRight className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Rabbit Hole</h3>
              <p className="text-xs text-zinc-500">Follow the creative lineage</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {parents.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 pl-1">↑ Parents ({parents.length})</p>
              <div className="space-y-1.5">{parents.map(p => <LineageNode key={p.id} p={p} />)}</div>
            </div>
          )}

          <div className="mb-3">
            {parents.length > 0 && <div className="w-px h-4 bg-purple-500/30 ml-8 mb-1" />}
            <LineageNode p={project} isCurrent />
            {children.length > 0 && <div className="w-px h-4 bg-purple-500/30 ml-8 mt-1" />}
          </div>

          {children.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2 pl-1">↓ Forks ({children.length})</p>
              <div className="space-y-1.5">
                {children.map(c => (
                  <div key={c.id}>
                    <LineageNode p={c} depth={1} />
                    {grandchildren.filter(gc => gc.parent_project_id === c.id || (gc.parent_project_ids || []).includes(c.id))
                      .slice(0, 2).map(gc => <div key={gc.id} className="mt-1"><LineageNode p={gc} depth={2} /></div>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {parents.length === 0 && children.length === 0 && (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">This is a root project — no lineage yet.</p>
              <p className="text-xs text-zinc-600 mt-1">Fork it to start a chain!</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800">
          <button onClick={onClose} className="w-full py-2.5 text-sm text-zinc-400 hover:text-white transition-colors rounded-xl hover:bg-zinc-800">
            Back to discovering
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// LOADING SKELETON
// ═══════════════════════════════════════════════════════════════

const CardSkeleton = () => (
  <div className="w-full max-w-lg mx-auto">
    <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden">
      <div className="h-64 sm:h-80 bg-zinc-800 animate-pulse" />
      <div className="p-6 space-y-3">
        <div className="h-7 bg-zinc-800 rounded-xl animate-pulse w-3/4" />
        <div className="h-4 bg-zinc-800 rounded-lg animate-pulse w-1/3" />
        <div className="h-4 bg-zinc-800 rounded-lg animate-pulse" />
        <div className="h-4 bg-zinc-800 rounded-lg animate-pulse w-5/6" />
        <div className="flex gap-2 pt-2">
          <div className="h-12 bg-zinc-800 rounded-xl animate-pulse flex-1" />
          <div className="h-12 bg-zinc-800 rounded-xl animate-pulse w-28" />
          <div className="h-12 bg-zinc-800 rounded-xl animate-pulse w-12" />
        </div>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════

const SurpriseMePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Feed multi-select
  const feedSel = useFeedSelection();

  // All projects loaded from API
  const [allProjects, setAllProjects]     = useState([]);
  // The ordered feed of cards shown to the user
  const [feed, setFeed]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [exhausted, setExhausted]         = useState(false); // seen all projects

  const [discoveryMode, setDiscoveryMode] = useState('random');
  const [activeZone, setActiveZone]       = useState(null);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [rabbitHoleProject, setRabbitHoleProject] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Track seen project IDs to avoid repeats
  const seenRef     = useRef(new Set());
  // IntersectionObserver sentinel at bottom of feed
  const sentinelRef = useRef(null);
  // Ref to current mode/zone/projects so the observer callback is always fresh
  const modeRef     = useRef({ mode: 'random', zone: null, projects: [] });
  // Ref-mirror of loadingMore so the observer callback never reads stale state
  const loadingMoreRef = useRef(false);
  const exhaustedRef   = useRef(false);

  // Keep modeRef in sync
  useEffect(() => {
    modeRef.current = { mode: discoveryMode, zone: activeZone, projects: allProjects };
  }, [discoveryMode, activeZone, allProjects]);

  // ── Fetch all projects ────────────────────────────────────────
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const res = await api.get('/projects', { params: { limit: 200 } });
        const projects = res.data?.projects || res.data || [];
        setAllProjects(projects);
        modeRef.current.projects = projects;

        const zoneParam = searchParams.get('zone');
        if (zoneParam && ZONES[zoneParam]) {
          setActiveZone(zoneParam);
          modeRef.current.zone = zoneParam;
        }

        // Seed the first batch
        if (projects.length > 0) {
          const batch = pickBatch(projects, 'random', zoneParam || null, BATCH_SIZE);
          setFeed(batch);
          batch.forEach(p => seenRef.current.add(p.id));
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // ── Reset feed when mode/zone changes ────────────────────────
  useEffect(() => {
    if (allProjects.length === 0) return;
    seenRef.current.clear();
    exhaustedRef.current = false;
    loadingMoreRef.current = false;
    setExhausted(false);
    setLoadingMore(false);
    const batch = pickBatch(allProjects, discoveryMode, activeZone, BATCH_SIZE);
    setFeed(batch);
    batch.forEach(p => seenRef.current.add(p.id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [discoveryMode, activeZone]);

  // ── Show back-to-top button ───────────────────────────────────
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setRabbitHoleProject(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // ── Pick a batch of projects ──────────────────────────────────
  const pickBatch = useCallback((projects, mode, zone, n) => {
    let pool = [...projects];

    if (zone) pool = pool.filter(p => getProjectZone(p).id === zone);

    // Prefer unseen
    const unseen = pool.filter(p => !seenRef.current.has(p.id));
    if (unseen.length === 0) {
      // All seen — allow repeats but mark exhausted
      setExhausted(true);
      pool = pool; // full pool again
    } else {
      pool = unseen;
    }

    // Sort pool by mode
    switch (mode) {
      case 'surprising':
        pool = pool.filter(p => p.parent_project_id || (p.parent_project_ids?.length > 0) || (p.fork_count || 0) > 0);
        if (pool.length === 0) pool = unseen.length > 0 ? unseen : [...projects];
        pool.sort((a, b) => (b.fork_count || 0) - (a.fork_count || 0));
        break;
      case 'crosszone':
        const crossPool = pool.filter(p => {
          const pZone = getProjectZone(p).id;
          return (p.parent_project_ids || []).some(pid =>
            projects.find(par => par.id === pid && getProjectZone(par).id !== pZone)
          );
        });
        if (crossPool.length > 0) pool = crossPool;
        break;
      case 'underrated':
        pool.sort((a, b) => {
          const ra = ((a.fork_count || 0) + 1) / ((a.view_count || 1));
          const rb = ((b.fork_count || 0) + 1) / ((b.view_count || 1));
          return rb - ra;
        });
        break;
      default: // random — shuffle
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
    }

    return pool.slice(0, n);
  }, []);

  // ── Append next batch (called by sentinel observer) ───────────
  const appendBatch = useCallback(() => {
    const { mode, zone, projects } = modeRef.current;
    if (projects.length === 0 || loadingMoreRef.current || exhaustedRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);

    setTimeout(() => {
      const batch = pickBatch(projects, mode, zone, BATCH_SIZE);
      if (batch.length === 0) {
        exhaustedRef.current = true;
        setExhausted(true);
        loadingMoreRef.current = false;
        setLoadingMore(false);
        return;
      }
      batch.forEach(p => seenRef.current.add(p.id));
      setFeed(prev => [...prev, ...batch]);
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }, 400);
  }, [pickBatch]); // no loadingMore dep — uses ref instead

  // ── IntersectionObserver on sentinel — set up ONCE, never torn down ──
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) appendBatch(); },
      { rootMargin: '300px' }
    );
    // sentinelRef may not be in the DOM yet on first render; poll briefly
    const attach = () => {
      if (sentinelRef.current) { obs.observe(sentinelRef.current); }
      else { setTimeout(attach, 50); }
    };
    attach();
    return () => obs.disconnect();
  }, []); // empty deps — observer lives for the lifetime of the page

  // ── Handlers ──────────────────────────────────────────────────
  const handleFork = useCallback((project) => {
    navigate(`/dashboard?fork=${project.id}`);
  }, [navigate]);

  const handleViewOnMap = useCallback((project) => {
    navigate(`/dashboard?focus=${project.id}&highlight=lineage`);
  }, [navigate]);

  const handleViewProject = useCallback((project) => {
    navigate(`/dashboard?project=${project.id}`);
  }, [navigate]);

  const handleFollowRabbitHole = useCallback((project) => {
    setRabbitHoleProject(project);
  }, []);

  const handleRabbitHoleSelect = useCallback((project) => {
    // Prepend the selected project to the top of the feed
    seenRef.current.add(project.id);
    setFeed(prev => [project, ...prev]);
    setRabbitHoleProject(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleModeChange = (newMode) => {
    setDiscoveryMode(newMode);
    setShowModeSelector(false);
  };

  const handleZoneChange = (zoneId) => {
    setActiveZone(zoneId);
    setShowModeSelector(false);
  };

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">Loading the cosmos...</p>
        </div>
      </div>
    );
  }

  if (allProjects.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Nothing to discover yet</h2>
          <p className="text-sm text-zinc-500 mb-6">Be the first to create a project and start the creative chain.</p>
          <button
            onClick={() => navigate('/dashboard?create=true')}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-500 transition-colors"
          >
            Create First Project
          </button>
        </div>
      </div>
    );
  }

  const currentMode = DISCOVERY_MODES.find(m => m.id === discoveryMode);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-zinc-950 relative">

      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.05) 0%, transparent 55%)'
        }} />
      </div>

      {/* ── Sticky top bar ── */}
      <div className="sticky top-0 z-40 bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-base font-bold text-white flex items-center gap-2">
                <Shuffle className="w-4 h-4 text-purple-400" />
                Surprise Me
              </h1>
              <p className="text-[10px] text-zinc-500">
                {feed.length} discovered
                {activeZone && ` · ${ZONES[activeZone]?.shortLabel}`}
                {exhausted && ' · Showing repeats'}
              </p>
            </div>
          </div>

          {/* Mode selector */}
          <div className="relative">
            <button
              onClick={() => setShowModeSelector(!showModeSelector)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-600 transition-colors"
            >
              <Filter className="w-3.5 h-3.5" />
              {currentMode?.label || 'Random'}
              <ChevronDown className={cn("w-3 h-3 transition-transform", showModeSelector && "rotate-180")} />
            </button>

            {showModeSelector && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowModeSelector(false)} />
                <div className="absolute top-full right-0 mt-1 w-64 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="p-2">
                    <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider px-2 py-1">Discovery Mode</p>
                    {DISCOVERY_MODES.map(mode => {
                      const Icon = mode.icon;
                      return (
                        <button
                          key={mode.id}
                          onClick={() => handleModeChange(mode.id)}
                          className={cn(
                            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left',
                            discoveryMode === mode.id ? 'bg-purple-500/15 text-purple-300' : 'text-zinc-300 hover:bg-zinc-800'
                          )}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{mode.label}</p>
                            <p className="text-[10px] text-zinc-500">{mode.description}</p>
                          </div>
                          {discoveryMode === mode.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400" />}
                        </button>
                      );
                    })}
                  </div>

                  <div className="border-t border-zinc-800 p-2">
                    <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider px-2 py-1">Zone Filter</p>
                    <div className="flex flex-wrap gap-1 px-2 py-1">
                      <button
                        onClick={() => handleZoneChange(null)}
                        className={cn(
                          'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                          !activeZone
                            ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                            : 'text-zinc-500 hover:text-zinc-300 border-zinc-700'
                        )}
                      >
                        All
                      </button>
                      {Object.entries(ZONES).map(([id, zone]) => (
                        <button
                          key={id}
                          onClick={() => handleZoneChange(id)}
                          className={cn(
                            'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                            activeZone === id ? '' : 'text-zinc-500 hover:text-zinc-300 border-zinc-700'
                          )}
                          style={activeZone === id
                            ? { backgroundColor: zone.color + '20', color: zone.color, borderColor: zone.color + '40' }
                            : {}}
                        >
                          {zone.shortLabel}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Feed ── */}
      <div className="max-w-lg mx-auto px-4 pt-8 pb-24 space-y-8">
        {feed.map((project, i) => (
          <SurpriseCard
            key={`${project.id}-${i}`}
            project={project}
            index={i}
            onFork={handleFork}
            onViewOnMap={handleViewOnMap}
            onViewProject={handleViewProject}
            onFollowRabbitHole={handleFollowRabbitHole}
            onSelect={feedSel.toggle}
            isSelected={feedSel.isSelected(project.id)}
          />
        ))}

        {/* Loading more skeletons */}
        {loadingMore && (
          <div className="space-y-8">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        )}

        {/* Exhausted state — all projects seen */}
        {exhausted && !loadingMore && (
          <div className="text-center py-10">
            <div className="w-10 h-10 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-sm font-medium text-zinc-400 mb-1">You've seen everything!</p>
            <p className="text-xs text-zinc-600">Showing projects again from the top.</p>
          </div>
        )}

        {/* IntersectionObserver sentinel */}
        <div ref={sentinelRef} className="h-1" aria-hidden="true" />
      </div>

      {/* ── Back to top button ── */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={cn(
            'fixed bottom-24 md:bottom-8 right-4 md:right-8 z-40',
            'w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 shadow-lg',
            'flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-600',
            'transition-all duration-200 hover:scale-110',
          )}
          title="Back to top"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      )}

      {/* ── Rabbit hole overlay ── */}
      {rabbitHoleProject && (
        <RabbitHolePanel
          project={rabbitHoleProject}
          projects={allProjects}
          onSelect={handleRabbitHoleSelect}
          onClose={() => setRabbitHoleProject(null)}
        />
      )}

      <style>{`
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>

      {/* Multi-select bar */}
      <FeedSelectionBar {...feedSel} />
    </div>
  );
};

export default SurpriseMePage;